import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  HStack,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
} from '@chakra-ui/react'
import type { DetailTab } from '../capacity.types'
import {
  CAPACITY_UNIT_BY_LABEL,
  formatQuantity,
  formatQuantityPair,
  formatZeitzielIndex,
  parseNumber,
  serviceLabel,
} from '../capacity.utils'
import { ZeitzielIndexCard } from './ZeitzielCard'
import { CapacityBarChart, CapacityProgressChart } from './CapacityCharts'
import { CapacityAreaMap } from './CapacityAreaMap'
import { getCapacityBank, adaptBankItem, adaptAssignment } from '../../../../api/capacity.api'
import type { CapacityBankDetailDto } from '../../../../api/dto/capacity.dto'
import type { SibCapacityRow } from '../capacity.types'

export function BankDetailPanel({
  customerId,
  year,
  sibRows = [],
  submittedCustomerIds,
  onOpenSibDetail,
}: {
  customerId: number
  /** The planning year to show; omitted for the running year (SIB-928). */
  year?: number
  sibRows?: SibCapacityRow[]
  submittedCustomerIds?: Set<number>
  onOpenSibDetail: (userId: string) => void
}) {
  const [dto, setDto] = useState<CapacityBankDetailDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<DetailTab>('assignment')
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    setError(null)
    getCapacityBank(customerId, year)
      .then(setDto)
      .catch(e => setError(e?.message || 'Fehler beim Laden der Daten'))
      .finally(() => setLoading(false))
  }, [customerId, year])

  if (loading) {
    return (
      <Stack gap={4}>
        <Skeleton h="120px" borderRadius="md" />
        <Skeleton h="200px" borderRadius="md" />
        <Skeleton h="160px" borderRadius="md" />
      </Stack>
    )
  }

  if (error || !dto) {
    return (
      <Alert.Root status="error" variant="subtle">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Fehler beim Laden</Alert.Title>
          <Alert.Description>{error ?? 'Unbekannter Fehler'}</Alert.Description>
        </Alert.Content>
      </Alert.Root>
    )
  }

  const bank = adaptBankItem(dto)
  const relatedAssignments = dto.assignments.map(adaptAssignment)
  const isRed = bank.status === 'red'
  const hasSubmittedOrders = submittedCustomerIds?.has(customerId) ?? false

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ base: '1fr', xl: 'minmax(0, 2fr) minmax(0, 1fr)' }}
      gap={6}
      alignItems="start"
    >
      <Stack gap={4}>
        {isRed && (
          <Alert.Root status="error" variant="subtle">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Keine aktive Bestellung</Alert.Title>
              <Alert.Description>
                Für diese Bank liegt aktuell keine aktive Bestellung vor.
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}
        {!isRed && hasSubmittedOrders && (
          <Alert.Root status="warning" variant="subtle">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Offene Bestellung vorhanden</Alert.Title>
              <Alert.Description>
                Für diese Bank gibt es mindestens eine eingereichte Bestellung, die noch nicht
                bestätigt wurde.
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}

        <Tabs.Root
          value={tab}
          onValueChange={d => d.value && setTab(d.value as DetailTab)}
          size="sm"
          variant="subtle"
          colorPalette="gray"
        >
          <Tabs.List flexWrap="wrap">
            <Tabs.Indicator />
            {[
              { value: 'assignment', label: 'Zuordnung' },
              { value: 'trend', label: 'Verlauf' },
              { value: 'area', label: 'Wirkungsbereich' },
            ].map(item => (
              <Tabs.Trigger key={item.value} value={item.value}>
                {item.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs.Root>

        {/* {tab === 'overview' && (
          <Stack gap={4}>
            <ZeitzielIndexCard value={bank.target} />
            <Box
              display="grid"
              gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }}
              gap={4}
            >
              {(['Bestellt', 'Durchgeführt'] as const).map(cardLabel => (
                <Card.Root key={cardLabel} variant="outline" borderColor="border" bg="bg">
                  <Card.Header>
                    <Text fontSize="sm" fontWeight="semibold" color="fg">
                      {cardLabel}
                    </Text>
                  </Card.Header>
                  <Card.Body>
                    <Stack gap={2}>
                      {bank.ordered.map((_, i) => {
                        const label = serviceLabel(i)
                        const values = cardLabel === 'Bestellt' ? bank.ordered : bank.done
                        return (
                          <HStack key={label} justify="space-between" align="center">
                            <Text fontSize="sm" color="fg.subtle">
                              {label}
                            </Text>
                            <Text fontSize="sm" fontWeight="semibold" color="fg">
                              {formatQuantity(values[i] ?? '—', CAPACITY_UNIT_BY_LABEL[label])}
                            </Text>
                          </HStack>
                        )
                      })}
                    </Stack>
                  </Card.Body>
                </Card.Root>
              ))}
            </Box>

            <CapacityBarChart title={bank.bank} ordered={bank.ordered} done={bank.done} />
          </Stack>
        )} */}

        {tab === 'trend' && (
          <Stack gap={4}>
            <CapacityProgressChart
              title={bank.bank}
              ordered={bank.ordered}
              done={bank.done}
              monthlyData={dto.monthlyProgress}
            />
            <Card.Root variant="outline" borderColor="border" bg="bg">
              <Card.Body py={14} display="flex" alignItems="center" justifyContent="center">
                <Text fontSize="sm" color="fg.subtle" textAlign="center" maxW="360px">
                  Monatlicher Verlauf steht nach Integration der Backend-Daten zur Verfügung.
                </Text>
              </Card.Body>
            </Card.Root>
          </Stack>
        )}

        {tab === 'area' && (
          <Card.Root variant="outline" borderColor="border" bg="bg" overflow="hidden">
            <CapacityAreaMap
              focus={{ name: bank.bank, place: bank.place, type: 'bank' }}
              others={dto.assignments.map(a => ({
                name: a.sibName,
                place:
                  sibRows.find(r => r.userId === a.sibUserId)?.place ??
                  [a.sibPostal, a.sibCity].filter(Boolean).join(' '),
                type: 'sib' as const,
              }))}
            />
          </Card.Root>
        )}

        {tab === 'assignment' && (
          <Stack gap={3}>
            {relatedAssignments.length === 0 ? (
              <Card.Root variant="outline" borderColor="border" bg="bg">
                <Card.Body py={12} display="flex" alignItems="center" justifyContent="center">
                  <Text fontSize="sm" color="fg.subtle">
                    Keine Zuordnungen vorhanden.
                  </Text>
                </Card.Body>
              </Card.Root>
            ) : (
              relatedAssignments.map((assignment, index) => {
                const dtoAssignment = dto.assignments[index]
                const hasBadZeitziel = assignment.target.some(target => parseNumber(target) < 85)

                return (
                  <Card.Root
                    key={`${assignment.bank}-${assignment.sib}`}
                    variant="outline"
                    borderColor="border"
                    bg="bg"
                  >
                    <Card.Body>
                      <Stack gap={3}>
                        <HStack justify="space-between" align="start" gap={3}>
                          <Stack gap={0.5}>
                            <Text fontSize="sm" fontWeight="semibold" color="fg">
                              {assignment.sib}
                            </Text>
                            <Text fontSize="xs" color="fg.subtle">
                              Laufzeit bis {assignment.until}
                            </Text>
                          </Stack>

                          <Button
                            size="sm"
                            variant={hasBadZeitziel ? 'solid' : 'outline'}
                            colorPalette={hasBadZeitziel ? 'red' : 'gray'}
                            onClick={() =>
                              dtoAssignment.sibUserId && onOpenSibDetail(dtoAssignment.sibUserId)
                            }
                          >
                            SIB prüfen
                          </Button>
                        </HStack>
                        <Stack gap={0}>
                          {(bank.showPilot ? [0, 1, 2, 3] : [0, 1, 2]).map(i => {
                            const label = serviceLabel(i)
                            const ordered = assignment.ordered[i] ?? '0'
                            const done = assignment.done[i] ?? '0'
                            const target = assignment.target[i] ?? '—'
                            const donePct = `${Math.round((parseNumber(done) / Math.max(parseNumber(ordered), 1)) * 100)} %`
                            return (
                              <HStack
                                key={label}
                                justify="space-between"
                                align="center"
                                gap={4}
                                py={3}
                                borderTopWidth={i === 0 ? '0' : '1px'}
                                borderColor="border"
                                wrap="wrap"
                              >
                                <Text fontSize="sm" fontWeight="semibold" color="fg" minW="80px">
                                  {label}
                                </Text>
                                <HStack gap={6} wrap="wrap" justify="end">
                                  <Stack gap={0.5}>
                                    <Text fontSize="xs" color="fg.subtle">
                                      Durchgeführt/Bestellt
                                    </Text>
                                    <Text fontSize="sm" fontWeight="medium" color="fg">
                                      {formatQuantityPair(
                                        done,
                                        ordered,
                                        CAPACITY_UNIT_BY_LABEL[label]
                                      )}
                                    </Text>
                                  </Stack>
                                  <Stack gap={0.5}>
                                    <Text fontSize="xs" color="fg.subtle">
                                      Zeitziel
                                    </Text>
                                    <Text fontSize="sm" fontWeight="medium" color="fg">
                                      {formatZeitzielIndex(target)}
                                    </Text>
                                  </Stack>
                                  <Stack gap={0.5}>
                                    <Text fontSize="xs" color="fg.subtle">
                                      Gesamtfortschritt
                                    </Text>
                                    <Text fontSize="sm" fontWeight="medium" color="fg">
                                      {donePct}
                                    </Text>
                                  </Stack>
                                </HStack>
                              </HStack>
                            )
                          })}
                        </Stack>
                      </Stack>
                    </Card.Body>
                  </Card.Root>
                )
              })
            )}
          </Stack>
        )}
      </Stack>

      <BankAside dto={dto} />
    </Box>
  )
}

function BankAside({ dto }: { dto: CapacityBankDetailDto }) {
  const bank = adaptBankItem(dto)

  const serviceCount = bank.ordered.length
  const summary = Array.from({ length: serviceCount }, (_, i) => {
    const code = (['TOJ', 'CONSULTING', 'TRAINING', 'PILOT'] as const)[i]!
    const svcEntry = dto.services.find(s => s.serviceCode === code)
    return {
      label: serviceLabel(i),
      ordered: svcEntry?.orderedUnits ?? 0,
      done: svcEntry?.doneUnits ?? 0,
    }
  })

  return (
    <Stack gap={4}>
      <Card.Root variant="outline" borderColor="border" bg="bg">
        <Card.Body>
          <Stack gap={4}>
            <HStack gap={3} align="center">
              <Avatar.Root size="xl" variant="subtle" colorPalette="purple">
                <Avatar.Fallback name={bank.bank} />
              </Avatar.Root>
              <Box minW={0}>
                <Text
                  fontSize="xl"
                  fontWeight="semibold"
                  color="fg"
                  lineHeight="short"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {bank.bank}
                </Text>
              </Box>
            </HStack>

            <SimpleGrid columns={{ base: 2, xl: 3 }} gap={3}>
              {(
                [
                  ['BLZ', bank.blz],
                  ['PLZ / Ort', bank.place],
                  ['Vxx', bank.region],
                  ['Bestellung', bank.status === 'red' ? 'keine aktiv' : 'aktiv'],
                  ['Aktive SIBs', bank.sibs],
                ] as [string, string][]
              ).map(([label, value]) => (
                <Stack key={label} gap={1} minW={0}>
                  <Text fontSize="xs" color="fg.subtle">
                    {label}
                  </Text>
                  <HStack gap={2} align="center">
                    <Text fontSize="sm" fontWeight="medium" color="fg" lineHeight="short">
                      {value}
                    </Text>
                    {label === 'Aktive SIBs' && bank.status === 'yellow' ? (
                      <Badge colorPalette="yellow" variant="subtle" size="sm">
                        1 prüfen
                      </Badge>
                    ) : null}
                  </HStack>
                </Stack>
              ))}
            </SimpleGrid>
          </Stack>
        </Card.Body>
      </Card.Root>

      <Card.Root variant="outline" borderColor="border" bg="bg">
        <Card.Body>
          <Stack gap={3}>
            <Text fontSize="sm" fontWeight="semibold" color="fg">
              Leistungsübersicht
            </Text>
            <Stack gap={0}>
              {summary.map((row, index) => (
                <Stack
                  key={row.label}
                  gap={3}
                  py={3}
                  borderTopWidth={index === 0 ? 0 : '1px'}
                  borderColor="border"
                >
                  <Text fontSize="sm" fontWeight="semibold" color="fg">
                    {row.label}
                  </Text>
                  <SimpleGrid columns={{ base: 1, xl: 2 }} gap={3}>
                    <Stack gap={1} minW={0}>
                      <Text fontSize="xs" color="fg.subtle">
                        Bestellt
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" color="fg">
                        {formatQuantity(String(row.ordered), CAPACITY_UNIT_BY_LABEL[row.label])}
                      </Text>
                    </Stack>
                    <Stack gap={1} minW={0}>
                      <Text fontSize="xs" color="fg.subtle">
                        Durchgeführt
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" color="fg">
                        {formatQuantity(String(row.done), CAPACITY_UNIT_BY_LABEL[row.label])}
                      </Text>
                    </Stack>
                  </SimpleGrid>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Card.Body>
      </Card.Root>
    </Stack>
  )
}
